/**
 * Payment Entity Schemas
 *
 * The money core's polymorphic charge record: one row per charge attempt,
 * pointing at what it is for via a soft-FK + discriminator (`sourceType` +
 * `sourcePublicId`), mirroring `SignableRequirement`'s host-polymorphism
 * (`packages/shared-types/src/schemas/entities/signable-requirement.ts`).
 * New chargeable things never add payments plumbing — they just point at
 * this table. Underlying Prisma model: `Payment` (apps/backend).
 *
 * `status` lifecycle: `awaiting_payment -> paid -> refunded |
 * partially_refunded`, with `failed` and `expired` terminal-for-attempt
 * states. Stripe webhooks are the only writer of `paid` — never the client.
 *
 * Money amounts are integer minor units (cents); currency is USD-only for
 * the MVP. `subtotalCents` is the pre-fee price; `stripeFeeCents` /
 * `applicationFeeCents` are the two-layer fee split the fee engine computes;
 * `totalCents` is what the buyer is charged.
 */

import { z } from 'zod'

export const PaymentStatusSchema = z.enum([
  'awaiting_payment',
  'paid',
  'failed',
  'expired',
  'refunded',
  'partially_refunded'
])
export type PaymentStatus = z.infer<typeof PaymentStatusSchema>

/**
 * Discriminator half of the Payment polymorphism. `collection` is reserved
 * for the generic-collection consumer (deferred follow-up work) so the enum
 * ships complete now rather than needing a later migration.
 */
export const PaymentSourceTypeSchema = z.enum(['ticketed_event_order', 'collection'])
export type PaymentSourceType = z.infer<typeof PaymentSourceTypeSchema>

export const PaymentSchema = z.object({
  /** Payment publicId. */
  id: z.string(),
  sourceType: PaymentSourceTypeSchema,
  /** Soft-FK publicId of the source (e.g. a TicketedEventOrder publicId). */
  sourcePublicId: z.string(),
  /** SchoolPaymentAccount publicId that receives the funds. */
  paymentAccountId: z.string(),
  subtotalCents: z.number().int(),
  stripeFeeCents: z.number().int(),
  applicationFeeCents: z.number().int(),
  totalCents: z.number().int(),
  currency: z.string(),
  status: PaymentStatusSchema,
  stripePaymentIntentId: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string().nullable()
})
export type Payment = z.infer<typeof PaymentSchema>
